import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Navbar from "./components/Navbar";
import SearchComponent from "./components/SearchComponent";
import Sidebar from "./components/Sidebar";
import PageContent from "./components/PageContent";
import {
  getAllHelpPageData,
  getSearchedResult,
} from "../../Redux/actions/HelpingPageAction";
import { postRequest } from "../../services/axios";
import { toast } from "react-toastify";
import {
  findArticleById,
  findArticleByPath,
  findCategoryBySlug,
  findSubheadingBySlug,
  helpArticlePath,
  helpHomePath,
  isMongoId,
} from "./helpRoutes";
import "./Styles/style.css";

const HelpingPageLayout = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const toastTimerRef = useRef(null);
  const lastFetchedIdRef = useRef("");
  const { allHelpPageContent, searchedResult } = useSelector(
    (state) => state.HelpingPageReducer
  );
  const [loading, setLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [supportTopic, setSupportTopic] = useState("");
  const [helpToast, setHelpToast] = useState("");
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [openSubheadingId, setOpenSubheadingId] = useState(null);
  const [tertiaryId, setTertiaryId] = useState("");
  const [currPageContent, setCurrPageContent] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadPageContent, setLoadPageContent] = useState(false);
  const [mainHeading, setMainHeading] = useState("Getting Started");
  const [secondaryHeading, setSecondaryHeading] = useState("");
  const [tertiarySubHeading, setTertiarySubHeading] = useState("");
  const [headingClicked, setHeadingClicked] = useState(true);

  const articleSlug = params.article || "";
  const subheadingSlug = params.subheading || "";
  const tertiaryIdParam = isMongoId(params.tertiaryId) ? params.tertiaryId : "";
  const rawCategory = params.category || params.id || "";
  const mongoId =
    isMongoId(rawCategory) && !subheadingSlug && !articleSlug ? rawCategory : "";
  const categorySlug = mongoId ? "" : rawCategory;
  const articleTertiaryId = tertiaryIdParam || mongoId;
  const isArticleView = Boolean(articleSlug || articleTertiaryId);
  const isCategoryView = Boolean(categorySlug) && !isArticleView;

  const fetchContent = async () => {
    try {
      setLoading(true);
      dispatch(getAllHelpPageData()).then(() => {
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const toggleSubheading = (parentHeadingId, subHeadingId) => {
    const newOpenId = `${parentHeadingId}-${subHeadingId}`;
    setOpenSubheadingId((prevId) => (prevId === newOpenId ? null : newOpenId));

    const newSelectedHeading = allHelpPageContent.find(
      (data) => data._id === parentHeadingId
    );
    setSelectedHeading(newSelectedHeading);
  };

  const applyArticleResponse = (contentId, responseData, skipNavigate = false) => {
    const tertiary = responseData.tertiarySubHeading;
    const tertiaryDocId = String(tertiary?._id || contentId);
    setCurrPageContent(tertiary.content);
    setTertiarySubHeading(tertiary.tertiarySubHeading);
    setSecondaryHeading(responseData.secondaryHeading);
    setMainHeading(responseData.mainHeading);
    const matchedHeading = allHelpPageContent?.find(
      (heading) => String(heading._id) === String(responseData.mainId)
    );
    if (matchedHeading) setSelectedHeading(matchedHeading);
    setOpenSubheadingId(`${responseData.mainId}-${responseData.subHeadingId}`);
    setTertiaryId(tertiaryDocId);
    setHeadingClicked(false);
    setIsMenuOpen(false);
    setIsSearchOpen(false);
    lastFetchedIdRef.current = tertiaryDocId;
    document.title = `${tertiary.tertiarySubHeading} | ScrollX Help`;

    const nextPath = helpArticlePath(
      responseData.mainHeading,
      responseData.secondaryHeading,
      tertiary.tertiarySubHeading,
      tertiaryDocId
    );
    if (!skipNavigate && window.location.pathname !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  };

  const fetchContentById = async (contentId, { skipNavigate = false } = {}) => {
    try {
      setLoadPageContent(true);
      const response = await postRequest(`/help/getContentById/${contentId}`);
      if (response.data.success) {
        setLoadPageContent(false);
        applyArticleResponse(
          response.data.tertiarySubHeading?._id || contentId,
          response.data,
          skipNavigate
        );
      }
      toast.success("Content fetched");
    } catch (err) {
      setLoadPageContent(false);
      toast.error("Error in fetching content");
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (!allHelpPageContent?.length) return;

    if (articleTertiaryId) {
      const found = findArticleById(allHelpPageContent, articleTertiaryId);
      if (found) {
        setSelectedHeading(found.heading);
        setMainHeading(found.category);
        setOpenSubheadingId(`${found.headingId}-${found.subId}`);
        if (lastFetchedIdRef.current === String(found.id) && currPageContent) return;
        fetchContentById(found.id, { skipNavigate: Boolean(tertiaryIdParam) });
        return;
      }
      if (lastFetchedIdRef.current === String(articleTertiaryId) && currPageContent) return;
      fetchContentById(articleTertiaryId, { skipNavigate: Boolean(tertiaryIdParam) });
      return;
    }

    if (articleSlug && categorySlug) {
      const found = findArticleByPath(
        allHelpPageContent,
        categorySlug,
        articleSlug,
        subheadingSlug
      );
      if (!found) return;
      navigate(found.path, { replace: true });
      return;
    }

    if (isCategoryView) {
      const heading = findCategoryBySlug(allHelpPageContent, categorySlug);
      if (heading) {
        setSelectedHeading(heading);
        setMainHeading(heading.heading);
        setHeadingClicked(true);
        setTertiaryId("");
        setCurrPageContent("");
        lastFetchedIdRef.current = "";
        if (subheadingSlug) {
          const sub = findSubheadingBySlug(heading, subheadingSlug);
          if (sub) {
            setOpenSubheadingId(`${heading._id}-${sub._id}`);
            document.title = `${sub.subHeading} | ScrollX Help`;
          } else {
            setOpenSubheadingId(null);
            document.title = `${heading.heading} | ScrollX Help`;
          }
        } else {
          setOpenSubheadingId(null);
          document.title = `${heading.heading} | ScrollX Help`;
        }
      }
      return;
    }

    setHeadingClicked(true);
    setTertiaryId("");
    lastFetchedIdRef.current = "";
    document.title = "ScrollX Help Center";
  }, [allHelpPageContent, articleSlug, categorySlug, subheadingSlug, articleTertiaryId, tertiaryIdParam, isCategoryView]);

  useEffect(() => {
    if (allHelpPageContent && allHelpPageContent.length > 0) {
      setSelectedHeading((current) => current || allHelpPageContent[0]);
    }
  }, [allHelpPageContent]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleSearch = () => {
    setSearchLoading(true);
    if (debouncedQuery !== "") {
      dispatch(getSearchedResult(searchQuery)).then(() => {
        setSearchLoading(false);
      });
    } else {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
  }, [debouncedQuery, dispatch]);

  const handleSearchResultClick = (resultId) => {
    const found = findArticleById(allHelpPageContent, resultId);
    if (found) {
      navigate(found.path);
      return;
    }
    fetchContentById(resultId);
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setIsSearchOpen(true);
      }
      if (event.key === "Escape") {
        setIsSearchOpen(false);
        setIsSupportOpen(false);
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const showHelpToast = (message) => {
    setHelpToast(message);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setHelpToast(""), 3200);
  };

  const goHome = () => {
    setIsMenuOpen(false);
    setTertiaryId("");
    setCurrPageContent("");
    setTertiarySubHeading("");
    setSecondaryHeading("");
    setHeadingClicked(true);
    lastFetchedIdRef.current = "";
    document.title = "ScrollX Help Center";
    navigate(helpHomePath);
  };

  const copySupportEmail = async () => {
    try {
      await navigator.clipboard.writeText("support@scrollx.io");
      showHelpToast(
        `Support email copied${supportTopic ? ` for: ${supportTopic}` : ""}.`
      );
    } catch {
      showHelpToast("Support email: support@scrollx.io");
    }
    setIsSupportOpen(false);
  };

  const recordFeedback = (value) => {
    const key = tertiaryId || articleTertiaryId || articleSlug;
    if (key) localStorage.setItem(`scrollx-help-feedback:${key}`, value);
    showHelpToast(
      value === "yes"
        ? "Thanks — your feedback was saved."
        : "Thanks — we’ll use this to improve the article."
    );
  };

  return (
    <div className={`help-center${isMenuOpen ? " menu-open" : ""}`}>
      <a className="skip-link" href="#main-content">Skip to article</a>

      <Navbar
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        onHome={goHome}
        onSupport={() => setIsSupportOpen(true)}
      />

      <div className="shell">
        <aside className="sidebar" aria-label="Help navigation">
          <Sidebar
            helpPageData={allHelpPageContent}
            setSelectedHeading={setSelectedHeading}
            toggleSubheading={toggleSubheading}
            openSubheadingId={openSubheadingId}
            setOpenSubheadingId={setOpenSubheadingId}
            tertiaryId={tertiaryId}
            setTertiaryId={setTertiaryId}
            setHeadingClicked={setHeadingClicked}
            loadPageContent={loadPageContent}
            setMainHeading={setMainHeading}
          />
        </aside>

        <button
          className="backdrop"
          type="button"
          aria-label="Close navigation"
          onClick={() => setIsMenuOpen(false)}
        />

        <main id="main-content" tabIndex={-1}>
          <div className="content-wrap">
            <PageContent
              currPageContent={currPageContent}
              selectedHeading={selectedHeading}
              helpPageData={allHelpPageContent}
              toggleSubheading={toggleSubheading}
              openSubheadingId={openSubheadingId}
              tertiaryId={tertiaryId}
              setTertiaryId={setTertiaryId}
              loadPageContent={loadPageContent}
              secondaryHeading={secondaryHeading}
              tertiarySubHeading={tertiarySubHeading}
              mainHeading={mainHeading}
              headingClicked={headingClicked}
              setCurrPageContent={setCurrPageContent}
              setHeadingClicked={setHeadingClicked}
              setIsSearchOpen={setIsSearchOpen}
              setSearchQuery={setSearchQuery}
              loading={loading}
              onGoHome={goHome}
              onOpenSupport={() => setIsSupportOpen(true)}
              onFeedback={recordFeedback}
              isArticleView={isArticleView}
              isCategoryView={isCategoryView}
              categorySlug={categorySlug}
            />
          </div>
        </main>
      </div>

      {isSearchOpen ? (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="searchLabel"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsSearchOpen(false);
          }}
        >
          <SearchComponent
            searchedResult={searchedResult}
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
            searchLoading={searchLoading}
            onResultClick={handleSearchResultClick}
            setIsSearchOpen={setIsSearchOpen}
            setHeadingClicked={setHeadingClicked}
          />
        </div>
      ) : null}

      {isSupportOpen ? (
        <div
          className="overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="supportTitle"
          onClick={(event) => {
            if (event.target === event.currentTarget) setIsSupportOpen(false);
          }}
        >
          <div className="dialog support-dialog">
            <h2 id="supportTitle">How can we help?</h2>
            <p>Choose the closest topic. This mockup keeps support actions local and does not send information.</p>
            <div className="support-options">
              {[
                ["Render issue", "Failed, stalled, or missing export"],
                ["Billing question", "Plans, payments, or cancellation"],
                ["Account access", "Sign-in and profile help"],
                ["Product feedback", "Share an idea or report a problem"],
              ].map(([topic, detail]) => (
                <button
                  className={`support-option${supportTopic === topic ? " is-selected" : ""}`}
                  type="button"
                  key={topic}
                  onClick={() => setSupportTopic(topic)}
                >
                  <strong>{topic}</strong>
                  <br />
                  <span>{detail}</span>
                </button>
              ))}
            </div>
            <div className="dialog-actions">
              <button className="button" type="button" onClick={() => setIsSupportOpen(false)}>Close</button>
              <button className="button primary" type="button" onClick={copySupportEmail}>Copy support email</button>
            </div>
          </div>
        </div>
      ) : null}

      {helpToast ? (
        <div className="toast" role="status" aria-live="polite">{helpToast}</div>
      ) : null}
    </div>
  );
};

export default HelpingPageLayout;
